import cx from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import YouTube from 'react-youtube';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  AUTH_TOKEN,
  CURRENT_USER,
  EMAIL_VERIFIED,
  REFRESH_TOKEN,
} from '../../constants/projectConstants';
import { apiGet } from '../../helpers/api';
import { getLocalStorage, youtubeParser } from '../../helpers/jsHelper';
import {
  learnByVideoActions,
  selectIsAutoplay,
  selectIsVideoModalCentered,
  selectShow,
  selectVideo,
  selectMattressDataVideo
} from '../../redux/slicers/learnByVideoSlicer';
import styles from './LearnVideoModel.module.scss';
import { TUTORIAL_API } from '../../config/constant/apiConstant';
import { dispatcher } from '../../helpers/projectHelper';
import { getAppState } from '../../redux/store';
import { warningToast } from '../../helpers/toastHelper';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import { icons } from '../../assets';
import { Portal } from '../../common/Portal';
import { debounce } from 'lodash';
import { isMattressBuilderPage } from '../admin/curator/ProductBuilderComponents/commonHelper';
import { selectCuratorInitFinished } from '../../redux/slicers/admin/curatorSlicer';
import { isCuratorPage } from '../../helpers/urlHelper';
const { CloseIconSmall, ExpandIcon, DragIcon } = icons;

export const openTutorialVideoModal = () => {
  const haveId = selectVideo(getAppState());
  if (!haveId) {
    warningToast('Tutorial video is not available right now.');
    return;
  }
  dispatcher(learnByVideoActions.show());
};

const initialPosition = { x: 0, y: 0 };

export default function LearnVideoModel() {
  const dispatch = useDispatch();
  const videoId = useSelector(selectVideo);
  const selectMattressDataVideoId = useSelector(selectMattressDataVideo);
  const showVideo = useSelector(selectShow);
  const isCentered = useSelector(selectIsVideoModalCentered);
  const isAutoplay = useSelector(selectIsAutoplay);
  const location = useLocation();
  const [player, setPlayer] = useState(null);
  const [playerState, setPlayerState] = useState(null);
  const [position, setPosition] = useState(initialPosition);
  const positionRef = useRef(position);
  positionRef.current = position;
  const videoContainerRef = useRef(null);
  const curatorInitialized = useSelector(selectCuratorInitFinished);

  useEffect(() => {
    const listener = debounce(() => {
      if (!videoContainerRef?.current) return;
      const clientRect = videoContainerRef?.current?.getBoundingClientRect();
      const { top, left, right } = clientRect;
      const { x, y } = positionRef.current;
      let newX = x;
      let newY = y;
      if (left < 0) {
        newX = x + Math.abs(left);
      } else {
        const rightDiff = right - window.innerWidth;
        if (rightDiff > 0) {
          if (rightDiff > left) {
            newX = x - left;
          } else {
            newX = x - rightDiff;
          }
        }
      }
      if (top < 0) {
        newY = y + Math.abs(top);
      }
      if (x !== newX || y !== newY) {
        setPosition({ x: newX, y: newY });
      }
      if (window.innerHeight < 400) {
        setPosition(initialPosition);
        dispatch(learnByVideoActions.setCentered(false));
      }
    }, 60);
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
      listener.cancel();
    };
  }, []);

  useEffect(() => {
    if (!videoContainerRef?.current) return;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const videoWidth = videoContainerRef?.current?.clientWidth || 0;
    const videoHeight = videoContainerRef?.current?.clientHeight || 0;
    const posX = (windowWidth - videoWidth) / 2;
    const posY = (windowHeight - videoHeight) / 2;
    setPosition(
      isCentered
        ? {
            x: posX,
            y: posY,
          }
        : initialPosition
    );
  }, [
    isCentered,
    videoContainerRef?.current?.clientWidth,
    videoContainerRef?.current?.clientHeight,
  ]);

  useEffect(() => {
    addRemoveListener(true);
    return () => {
      addRemoveListener(false);
    };
  }, [player, playerState]);

  useEffect(() => {
    // if (isMattressBuilderPage()) return; // Skip API call if on Mattress Builder page
    if (curatorInitialized || !isCuratorPage()) apiGetTutorialVideos(); // Fetch tutorial API if curator is initialized or not on curator page
  }, [location, curatorInitialized]);

  const apiGetTutorialVideos = () => {
    if (!checkUserHasAccess() || videoId) {
      return;
    }
    apiGet(TUTORIAL_API).then((response) => {
      if (response && response?.results?.length) {
        const url = youtubeParser(response?.results?.[0]?.url);
        dispatch(learnByVideoActions.setData(url));
      }
    });
  };

  const checkUserHasAccess = () => {
    if (
      getLocalStorage(AUTH_TOKEN) &&
      getLocalStorage(REFRESH_TOKEN) &&
      getLocalStorage(EMAIL_VERIFIED) &&
      getLocalStorage(CURRENT_USER)
    ) {
      return true;
    }
    return false;
  };

  const addRemoveListener = (flag) => {
    if (flag) {
      document.addEventListener('visibilitychange', handlePlayNPause);
    } else {
      document.removeEventListener('visibilitychange', handlePlayNPause);
    }
  };

  const handlePlayNPause = () => {
    if (document.hidden) {
      if (playerState === 2) {
        addRemoveListener(false);
        return;
      }
      player?.pauseVideo();
    } else {
      player?.playVideo();
    }
  };

  const onReady = (event) => {
    setPlayer(event.target);
  };

  const onStateChange = (event) => {
    setPlayerState(event.data);
  };

  const handleClose = () => {
    dispatch(learnByVideoActions.close());
    setPosition(initialPosition);
  };

  const toggleExpandAndCenter = () => {
    dispatch(learnByVideoActions.setCentered(!isCentered));
  };

  const onDrag = (e, position) => {
    setPosition({ x: position.x, y: position.y });
  };

  if (!showVideo) return null;

  return (
    <Portal>
      <Draggable bounds="parent" handle=".video-drag" onDrag={onDrag} position={position}>
        <div
          className={cx(styles.videoModal, !isCentered ? styles.small : '')}
          ref={videoContainerRef}
          style={isCentered ? { left: 0, top: 0 } : {}}>
          <Button onClick={toggleExpandAndCenter} className={cx('modalCloseButton', styles.scale)}>
            <ExpandIcon />
          </Button>
          <Button className={cx('modalCloseButton', 'video-drag', styles.drag)}>
            <DragIcon />
          </Button>
          <Button onClick={handleClose} className={cx('modalCloseButton', styles.close)}>
            <CloseIconSmall />
          </Button>
          <Video
            srcId={isMattressBuilderPage() ? selectMattressDataVideoId : videoId}
            autoplay={isAutoplay}
            onReady={onReady}
            onStateChange={onStateChange}
          />
        </div>
      </Draggable>
    </Portal>
  );
}

const Video = ({ srcId, autoplay = 0, onReady, onStateChange }) => {
  if (!srcId) return null;
  const opts = {
    playerVars: {
      autoplay: autoplay,
    },
  };

  return (
    <div className={styles.videoResponsive}>
      <YouTube videoId={srcId} opts={opts} onReady={onReady} onStateChange={onStateChange} />
    </div>
  );
};
